import { CaretDownSolidIcon, MagnifyingGlassRegularIcon } from "@elixir/icons";
import { useFuseSearch } from "@elixir/utils";
import { Dropdown, Empty } from "antd";
import { useMemo, useState } from "react";
import {
  CountryCode,
  CountryData,
  getCountryCodesData,
} from "../../constants/country-code-data";

type TPhoneCountryCodeDropdownProps = {
  value: string;
  onSelect?: (code: string, countryCode: CountryCode) => void;
  disabled?: boolean;
};

const PhoneCountryCodeDropdown = ({
  value,
  onSelect,
  disabled = false,
}: TPhoneCountryCodeDropdownProps) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState(false);

  const countryCodesData = useMemo(() => getCountryCodesData(), []);

  const filteredCountries = useFuseSearch(searchQuery, countryCodesData, [
    "countryNameEn",
    "countryCallingCode",
  ]);

  const searchCountries = useMemo(() => {
    if (searchQuery && searchQuery.length > 0) {
      return filteredCountries || [];
    }
    return countryCodesData;
  }, [filteredCountries, countryCodesData, searchQuery]);

  const handleCountrySelect = (country: CountryData) => {
    onSelect && onSelect(`+${country.countryCallingCode}`, country.countryCode);
    setShowDropdown(false);
    setSearchQuery("");
  };

  return (
    <Dropdown
      placement="bottomLeft"
      trigger={["click"]}
      open={showDropdown}
      disabled={disabled}
      onOpenChange={(visible) => {
        setShowDropdown(visible);
        if (!visible) {
          setSearchQuery("");
        }
      }}
      dropdownRender={() => (
        <div className="dr-bg-darwin-touch-neutral-0 dr-rounded-4 dr-shadow-elevation-50 dr-min-w-240">
          <div className="dr-px-16 dr-py-10 dr-flex dr-items-center dr-gap-8 dr-border-b dr-border-darwin-touch-neutral-200">
            <MagnifyingGlassRegularIcon className="dr-w-13 dr-h-13 dr-text-darwin-touch-neutral-800" />
            <input
              type="text"
              placeholder="Search country..."
              className="dr-placeholder-darwin-touch-neutral-800 dr-bg-white dr-outline-none dr-flex-1"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className="dr-max-h-200 dr-overflow-y-auto">
            {searchCountries.length > 0 ? (
              searchCountries.map((country) => (
                <div
                  key={country.countryCode}
                  onClick={() => handleCountrySelect(country)}
                  className="dr-px-16 dr-py-8 hover:dr-bg-darwin-touch-neutral-50 dr-cursor-pointer"
                >
                  <p className="dr-text-darwin-touch-neutral-900">
                    {country.countryNameEn} (+{country.countryCallingCode})
                  </p>
                </div>
              ))
            ) : (
              <Empty
                className="dr-my-12"
                description="No countries found"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </div>
        </div>
      )}
    >
      <div className="dr-cursor-pointer dr-flex dr-items-center dr-gap-8 dr-px-8 dr-h-full">
        <p className="dr-text-darwin-touch-neutral-900 dr-font-600">{value}</p>
        <CaretDownSolidIcon className="dr-w-8 dr-h-12 dr-text-darwin-touch-neutral-600" />
      </div>
    </Dropdown>
  );
};

export default PhoneCountryCodeDropdown;
